import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Check, Info, Star, Zap, Crown, Loader2, Mail, ArrowRight } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";

const LockInChoosePlan = () => {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);

  // Lock-In Beta Pricing Plans (matching the Lock-In landing page)
  const lockInPlans = [
    {
      key: 'starter_beta',
      name: 'Starter',
      price: 97,
      originalPrice: 291,
      emailQuota: 150000,
      overage: 10,
      features: [
        '3B contacts included',
        'AI finds prospects',
        'Self-improving system',
        'Email tracking',
        'Campaign analytics'
      ],
      description: 'Perfect for getting started',
      popular: false
    },
    {
      key: 'pro_beta',
      name: 'Pro',
      price: 297,
      originalPrice: 891,
      emailQuota: 500000,
      overage: 10,
      features: [
        'Everything in Starter',
        'Priority AI learning',
        'Advanced analytics',
        'Dedicated support',
        'White-label options'
      ],
      description: 'Most popular choice',
      popular: true
    },
    {
      key: 'scale_beta',
      name: 'Scale',
      price: 997,
      originalPrice: 2991,
      emailQuota: 5000000,
      overage: 10,
      features: [
        'Everything in Pro',
        'Dedicated AI training',
        'White-label options',
        'Custom integrations',
        'Enterprise support'
      ],
      description: 'For high-volume senders',
      popular: false
    }
  ];

  // Handle plan selection
  const handlePlanSelect = async (planKey: string) => {
    console.log('🎯 Lock-In plan selected:', planKey);
    
    try {
      setIsProcessing(true);
      setProcessingPlan(planKey);
      
      // Navigate to register page with the selected plan
      navigate('/register', { 
        state: { 
          selectedPlanId: planKey,
          directCheckout: true,
          isLockInPlan: true
        } 
      });

    } catch (error) {
      console.error('❌ Error during plan selection:', error);
      
      toast({
        title: "Error",
        description: "Failed to process plan selection. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
      setProcessingPlan(null);
    }
  };

  // Get plan icon
  const getPlanIcon = (planKey: string) => {
    switch (planKey) {
      case 'starter_beta':
        return <Zap className="h-6 w-6 text-green-600" />;
      case 'pro_beta':
        return <Star className="h-6 w-6 text-blue-600" />;
      case 'scale_beta':
        return <Crown className="h-6 w-6 text-purple-600" />;
      default:
        return <Mail className="h-6 w-6 text-primary" />;
    }
  };

  const renderPlanCard = (plan) => (
    <Card 
      key={plan.key} 
      className={`flex flex-col transition-all hover:shadow-xl h-full relative ${
        plan.popular
          ? 'border-primary border-2 shadow-lg scale-105' 
          : 'shadow-md border hover:border-primary/50'
      }`}
    >
      {plan.popular && (
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-green-600 to-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full shadow-md">
          🚀 MOST POPULAR
        </div>
      )}
      
      <CardHeader className="pt-8 pb-4 text-center">
        <div className="flex justify-center mb-3">
          <div className={`p-3 rounded-full ${plan.popular ? 'bg-gradient-to-r from-blue-100 to-green-100' : 'bg-primary/10'}`}>
            {getPlanIcon(plan.key)}
          </div>
        </div>
        <CardTitle className="text-2xl font-bold mb-2">{plan.name}</CardTitle>
        <div className="mb-3">
          <span className="text-4xl font-bold tracking-tight text-green-600">
            ${plan.price}
          </span>
          <span className="text-sm text-muted-foreground ml-1">/month</span>
        </div>
        <div className="text-sm font-medium text-primary mb-2">{plan.emailQuota.toLocaleString()} emails/month</div>
        <div className="text-xs text-red-500 line-through">Was ${plan.originalPrice}/month</div>
        <div className="text-xs text-green-600 font-semibold">Save ${plan.originalPrice - plan.price}/month</div>
        <CardDescription className="text-sm px-2 mt-2">{plan.description}</CardDescription>
      </CardHeader>
      
      <Separator />
      
      <CardContent className="flex-grow pt-6 pb-4">
        {/* Guarantee Badge for All Beta Plans */}
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-green-700">
            <Check className="h-4 w-4" />
            <span className="text-sm font-medium">30-Day Money Back Guarantee</span>
          </div>
        </div>
        
        <ul className="space-y-3 text-sm">
          {plan.features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <Check className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <span className="text-muted-foreground">{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>
      
      <CardFooter className="mt-auto pt-4 pb-6 px-6">
        <Button 
          className={`w-full ${plan.popular ? 'bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white`}
          onClick={() => handlePlanSelect(plan.key)}
          size="lg"
          disabled={isProcessing}
        >
          {isProcessing && processingPlan === plan.key ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            getPlanIcon(plan.key)
          )}
          {isProcessing && processingPlan === plan.key ? 'Processing...' : 
            `Lock In ${plan.name} - $${plan.price}/mo`
          }
        </Button>
      </CardFooter>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/40 flex flex-col items-center py-16 px-4">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 flex items-center justify-center">
              <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-8 w-8">
                <g>
                  {/* Left wing */}
                  <path d="M20 50 L70 0 L70 70 L50 90 L50 150 L70 130 L70 200 L20 150 Z" fill="#3B82F6"/>
                  {/* Center V */}
                  <path d="M70 70 L100 40 L130 70 L100 100 Z" fill="#60A5FA"/>
                  {/* Right wing */}
                  <path d="M130 70 L130 0 L180 50 L180 150 L130 200 L130 130 L150 150 L150 90 Z" fill="#3B82F6"/>
                </g>
              </svg>
            </div>
            <span className="font-bold text-xl">MUNGER</span>
          </div>
          <Button 
            onClick={() => window.location.href = "#/login"}
            variant="outline" 
            size="sm"
          >
            Sign In
          </Button>
        </div>
      </nav>

      <div className="text-center max-w-4xl mx-auto mb-16 mt-20">
        <Badge className="bg-red-500/20 text-red-300 border-red-500/30 mb-6">
          ⚡ Beta Pricing - Limited Time Only
        </Badge>
        
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
          Lock In <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">Beta Pricing</span>
        </h1>
        <p className="text-lg text-muted-foreground mb-8">
          These prices will never be available again. Lock in your rate forever before we launch.
        </p>
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Check className="h-4 w-4 text-green-500" />
          <span>Pricing locked forever</span>
          <span>•</span>
          <Check className="h-4 w-4 text-green-500" />
          <span>Save up to $1,994/month</span>
          <span>•</span>
          <Check className="h-4 w-4 text-green-500" />
          <span>30-day money back guarantee</span>
          <span>•</span>
          <Check className="h-4 w-4 text-green-500" />
          <span>Beta features included</span>
        </div>
      </div>

      {/* Money Back Guarantee Banner */}
      <div className="w-full max-w-6xl mb-8">
        <div className="bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/20 rounded-lg p-4">
          <div className="flex items-center justify-center gap-3 text-center">
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
              <Check className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-green-600 mb-1">30-Day Money Back Guarantee</h3>
              <p className="text-sm text-muted-foreground">
                Try our beta pricing risk-free. Get a full refund within 30 days, no questions asked.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Lock-In Beta Plans */}
      <div className="w-full max-w-6xl mb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {lockInPlans.map(renderPlanCard)}
        </div>
      </div>

      {/* Custom Plan Option */}
      <div className="w-full max-w-4xl mb-16">
        <Card className="border-2 border-dashed border-purple-500/50 bg-gradient-to-br from-purple-500/5 to-pink-500/5">
          <CardHeader className="text-center pb-6">
            <CardTitle className="text-2xl font-bold mb-2">Need Something Custom?</CardTitle>
            <CardDescription className="text-base">
              Contact our sales team for custom enterprise pricing and features.
            </CardDescription>
          </CardHeader>
          
          <CardContent className="text-center">
            <div className="bg-card border rounded-lg p-6 mb-6">
              <div className="text-3xl font-bold mb-2">Custom Pricing</div>
              <div className="text-sm text-muted-foreground mb-4">
                Unlimited emails • Custom AI models • Dedicated servers
              </div>
            </div>
          </CardContent>
          
          <CardFooter className="pt-6">
            <Button 
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              onClick={() => window.location.href = "mailto:sales@munger.ai"}
              size="lg"
            >
              <Mail className="h-4 w-4 mr-2" />
              Contact Sales
            </Button>
          </CardFooter>
        </Card>
      </div>

      {/* Pricing Information */}
      <Alert className="mt-12 max-w-4xl mx-auto bg-muted/50 border-dashed">
        <Info className="h-4 w-4" />
        <AlertTitle className="font-semibold">Risk-Free Beta Pricing</AlertTitle>
        <AlertDescription>
          <strong>30-Day Money Back Guarantee:</strong> Try beta pricing risk-free with full refund within 30 days.<br/>
          <strong>Limited time:</strong> Beta pricing ends when we officially launch.<br/>
          <strong>Price lock:</strong> Your rate is locked forever, even after launch.<br/>
          <strong>All plans:</strong> $10 per 10,000 emails beyond your plan limit.
        </AlertDescription>
      </Alert>

      {/* Contact Sales Section */}
      <div className="mt-12 text-center">
        <p className="text-muted-foreground mb-2">Questions about beta pricing or need enterprise features?</p>
        <Button variant="link" className="text-primary font-medium">
          Contact Sales • sales@munger.ai
        </Button>
      </div>
    </div>
  );
};

export default LockInChoosePlan; 